import { T_CONTEXT, T_REQUEST, T_RICH, T_ROCK, type World } from '../core/world';
import type { Camera } from './renderer';

const KIND_COLOR: Record<string, string> = {
  belt: '#3A4D63', users: '#59D98E', forge: '#A98BFF', bench: '#59D98E', specdesk: '#6DA8FF', indexer: '#C9D6E6',
  rack: '#F2D27A', monitor: '#7FE3FF', checkpoint: '#7FD6FF', nest: '#FF3DCE', monolith: '#9A8CB8', terminal: '#9CF2C0',
  cache: '#F2D27A', postmortem: '#FF8A7A', breaker: '#F2D27A', gate: '#7FE3E0', firewall: '#8A939E', router: '#9DB8DA', scanner: '#A0B4FF', drones: '#D6B8FF',
};

/** A small overview map. Click to jump the camera. */
export class Minimap {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private img: ImageData | null = null;
  private off: HTMLCanvasElement | null = null;
  private last = 0;
  private scale = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  bind(onJump: (x: number, y: number) => void) {
    const jump = (e: PointerEvent) => {
      const r = this.canvas.getBoundingClientRect();
      onJump((e.clientX - r.left) / this.scale, (e.clientY - r.top) / this.scale);
    };
    this.canvas.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      jump(e);
      const move = (ev: PointerEvent) => jump(ev);
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    });
  }

  draw(w: World, cam: Camera, now: number) {
    const maxW = 200;
    const maxH = 140;
    this.scale = Math.min(maxW / w.w, maxH / w.h);
    const cw = Math.round(w.w * this.scale);
    const ch = Math.round(w.h * this.scale);
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
      this.img = null;
    }
    const ctx = this.ctx;
    if (now - this.last > 400 || !this.img) {
      this.last = now;
      // Terrain into an offscreen ImageData at one pixel per tile, then scale.
      const id = new ImageData(w.w, w.h);
      const d = id.data;
      for (let i = 0; i < w.w * w.h; i++) {
        let r = 5, g = 7, b = 10;
        if (w.fog[i]) {
          const t = w.terrain[i];
          if (t === T_ROCK) [r, g, b] = [26, 31, 38];
          else if (t === T_REQUEST) [r, g, b] = [110, 84, 34];
          else if (t === T_CONTEXT) [r, g, b] = [24, 92, 84];
          else if (t === T_RICH) [r, g, b] = [36, 130, 118];
          else [r, g, b] = [14, 19, 26];
        }
        d[i * 4] = r;
        d[i * 4 + 1] = g;
        d[i * 4 + 2] = b;
        d[i * 4 + 3] = 255;
      }
      this.img = id;
      if (!this.off || this.off.width !== w.w || this.off.height !== w.h) {
        this.off = document.createElement('canvas');
        this.off.width = w.w;
        this.off.height = w.h;
      }
      this.off.getContext('2d')!.putImageData(id, 0, 0);
    }
    ctx.imageSmoothingEnabled = false;
    if (this.off) ctx.drawImage(this.off, 0, 0, cw, ch);
    for (const b of w.buildings.values()) {
      if (!w.revealed(b.x, b.y)) continue;
      ctx.fillStyle = b.corrupted ? '#FF3DCE' : KIND_COLOR[b.kind] ?? '#888';
      ctx.fillRect(b.x * this.scale, b.y * this.scale, Math.max(1, b.w * this.scale), Math.max(1, b.h * this.scale));
    }
    ctx.fillStyle = '#FF3DCE';
    for (const g of w.glitches) if (w.revealed(Math.floor(g.x), Math.floor(g.y))) ctx.fillRect(g.x * this.scale - 1, g.y * this.scale - 1, 2, 2);
    for (const z of w.zones) {
      ctx.strokeStyle = z.state === 'claimed' || z.state === 'approved' ? 'rgba(89,217,142,0.6)' : 'rgba(170,140,255,0.7)';
      ctx.strokeRect(z.x0 * this.scale + 0.5, z.y0 * this.scale + 0.5, (z.x1 - z.x0 + 1) * this.scale, (z.y1 - z.y0 + 1) * this.scale);
    }
    const [ax, ay] = cam.toWorld(0, 0);
    const [bx, by] = cam.toWorld(cam.vw, cam.vh);
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 1;
    ctx.strokeRect(ax * this.scale + 0.5, ay * this.scale + 0.5, (bx - ax) * this.scale, (by - ay) * this.scale);
  }
}
